import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AuthLayout from '../../features/auth/AuthLayout';
import LogoHeader from '../../components/LogoHeader';
import OneVerseLogo from '../../components/OneVerseLogo';
import AuthCard from '../../features/auth/AuthCard';
import PasswordRequirements from '../../features/auth/PasswordRequirements';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/API';
import { PASSWORD_RULES } from '@/features/auth/registration/schema';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);
  const navigate = useNavigate();

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email) {
      setError(t('auth.errors.email_required'));
      return;
    }

    setLoading(true);

    try {
      const response = await API.fetch('/auth/forgot_password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setMessage(data.message);
        setStep('confirm');
      }
    } catch (error) {
      console.error(error);
      setError(t('auth.errors.unexpected'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!confirmationCode || !newPassword || !confirmPassword) {
      setError(t('auth.errors.all_fields_required'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.errors.passwords_dont_match'));
      return;
    }

    const hasPasswordFailures = PASSWORD_RULES.some(r => !r.test(newPassword));
    if (hasPasswordFailures) {
      setShowPasswordRequirements(true);
      return;
    }
    setShowPasswordRequirements(false);

    setLoading(true);

    try {
      const response = await API.fetch('/auth/confirm_forgot_password', {
        method: 'POST',
        body: JSON.stringify({
          email,
          confirmation_code: confirmationCode,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setMessage(data.message);
        // Redirect to login after successful reset
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (error) {
      console.error(error);
      setError(t('auth.errors.unexpected'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <AuthLayout>
        <LogoHeader />
        <AuthCard>
          <OneVerseLogo className="mb-6 mt-2 mx-auto" />
          <h1 className="text-center text-2xl font-semibold text-gray-900">
            {step === 'request' ? t('auth.reset_password') : t('auth.enter_reset_code')}
          </h1>
          <p className="text-center text-gray-500 mb-6">
            {step === 'request'
              ? t('auth.reset_code_instructions')
              : t('auth.new_code_instructions')}
          </p>

          <div className="w-full max-w-md mx-auto p-6">
            {step === 'request' ? (
              <form onSubmit={handleRequestReset} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">
                    {t('auth.email')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="bg-input border-0 rounded-lg h-12 px-4"
                    placeholder={t('auth.email_placeholder')}
                    disabled={loading}
                  />
                </div>

                {error && <p className="text-red-500 text-center">{error}</p>}
                {message && (
                  <p className="text-green-600 text-center">{message}</p>
                )}

                <Button
                  type="submit"
                  className="w-full h-12 bg-primary hover:bg-primary-600 text-primary-foreground font-semibold rounded-lg text-base"
                  disabled={loading}
                >
                  {loading ? t('auth.sending') : t('auth.send_reset_code')}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleConfirmReset} className="space-y-6">
                <div className="space-y-2">
                  <Label
                    htmlFor="confirmationCode"
                    className="text-gray-700 font-medium"
                  >
                    {t('auth.confirmation_code')}
                  </Label>
                  <Input
                    id="confirmationCode"
                    type="text"
                    value={confirmationCode}
                    onChange={e => setConfirmationCode(e.target.value)}
                    className="bg-input border-0 rounded-lg h-12 px-4"
                    placeholder={t('auth.confirmation_code_placeholder')}
                    disabled={loading}
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="newPassword"
                    className="text-gray-700 font-medium"
                  >
                    {t('auth.new_password')}
                  </Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={e => {
                      setNewPassword(e.target.value);
                      setShowPasswordRequirements(false);
                    }}
                    className="bg-input border-0 rounded-lg h-12 px-4"
                    placeholder={t('auth.new_password_placeholder')}
                    disabled={loading}
                  />
                  <PasswordRequirements show={showPasswordRequirements} />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="confirmPassword"
                    className="text-gray-700 font-medium"
                  >
                    {t('auth.confirm_password')}
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className="bg-input border-0 rounded-lg h-12 px-4"
                    placeholder={t('auth.confirm_password_placeholder')}
                    disabled={loading}
                  />
                </div>

                {error && <p className="text-red-500 text-center">{error}</p>}
                {message && (
                  <p className="text-green-600 text-center">{message}</p>
                )}

                <Button
                  type="submit"
                  className="w-full h-12 bg-primary hover:bg-primary-600 text-primary-foreground font-semibold rounded-lg text-base"
                  disabled={loading}
                >
                  {loading ? t('auth.resetting') : t('auth.reset_password_action')}
                </Button>

                <Button
                  type="button"
                  onClick={() => setStep('request')}
                  variant="outline"
                  className="w-full h-12 border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg text-base"
                >
                  {t('app.back')}
                </Button>
              </form>
            )}

            <p className="mt-4 text-center text-sm text-gray-600">
              {t('auth.remember_password')}{' '}
              <Link
                to="/login"
                className="font-medium text-blue-700 hover:underline"
              >
                {t('auth.sign_in_button')}
              </Link>
            </p>
          </div>
        </AuthCard>
      </AuthLayout>
    </main>
  );
};

export default ForgotPassword;
